import React, { useState } from "react";

import { ArrowUpRightIcon, ImageIcon } from "@/components/icons";
import { Drawer } from "@/components/ui/Drawer";
import { PLATFORM_REGISTRY } from "@/config/platforms";
import { ENTITY_CONFIG } from "@/core/entityConfig";

import { formatCampaignDates, parseGraphTime, type DayPost } from "../utils/dayMarkers";
import { SOURCE_COLOR } from "./ReportViz";

/** A platform's icon in its own colour, named for screen readers. */
export const PlatformMark: React.FC<{ platform: string; size?: number }> = ({ platform, size = 12 }) => {
  const config = PLATFORM_REGISTRY[platform];
  if (!config) return null;
  const Icon = config.iconComponent;
  return <Icon size={size} style={{ color: config.color }} aria-label={config.label} />;
};

interface ReportPostDrawerProps {
  /** The open post; null closes the drawer. */
  post: DayPost | null;
  onClose: () => void;
  /** Radix's close focus hook, so the opener picks where focus goes back. */
  onCloseAutoFocus?: (event: Event) => void;
}

const WHEN = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Rome",
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const MONEY = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const when = (time: string): string | null => {
  const ms = parseGraphTime(time);
  return Number.isNaN(ms) ? null : WHEN.format(ms);
};

/** One post of the month's social report, from a chart pip: the image, when
 *  and where it went out, the whole caption, each platform's counts and a
 *  link to it there, and its boost. Built from the report alone: TouchPoint
 *  has no page of its own for one post, so there is no link inward. */
export const ReportPostDrawer: React.FC<ReportPostDrawerProps> = ({ post, onClose, onCloseAutoFocus }) => {
  // The drawer keeps the last post through its closing slide.
  const [shown, setShown] = useState<DayPost | null>(post);
  if (post && post !== shown) setShown(post);
  const p = post ?? shown;

  return (
    <Drawer
      isOpen={post !== null}
      onClose={onClose}
      title={ENTITY_CONFIG.post.label}
      a11yDescription="The post as the month's report read it: image, caption and engagement by platform."
      onCloseAutoFocus={onCloseAutoFocus}
      bodyPadding
      className="md:max-w-lg"
    >
      {p && <PostBody post={p} />}
    </Drawer>
  );
};

const PostBody: React.FC<{ post: DayPost }> = ({ post }) => {
  const published = when(post.publishedAt);
  return (
    <div className="space-y-5">
      <PostImage key={post.key} url={post.thumbnailUrl} />
      <div className="flex items-center gap-2 text-[13px] text-gray-500">
        {post.platforms.map((key) => (
          <PlatformMark key={key} platform={key} size={16} />
        ))}
        {published && <span className="tabular-nums">{published}</span>}
      </div>
      {post.caption ? (
        <p className="text-[14px] leading-relaxed text-gray-900 whitespace-pre-line [overflow-wrap:anywhere]">
          {post.caption}
        </p>
      ) : (
        <p className="text-[13px] text-gray-400">No caption.</p>
      )}
      {post.boost && (
        <div className="flex items-center gap-2 text-[13px] text-gray-700">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: SOURCE_COLOR.paid }}
          />
          <span>
            <span className="font-medium text-gray-900">Boosted</span>
            {" · "}
            {formatCampaignDates(post.boost.startDate, post.boost.endDate)}
            {post.boost.spend != null && (
              <span className="tabular-nums"> · {MONEY.format(post.boost.spend)}</span>
            )}
          </span>
        </div>
      )}
      <div className="divide-y divide-gray-100 border-t border-gray-100">
        {post.perPlatform.map((side) => {
          const config = PLATFORM_REGISTRY[side.platform];
          const label = config?.label ?? side.platform;
          const time = when(side.publishedAt);
          return (
            <section key={side.platform} className="py-3" aria-label={label}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                <PlatformMark platform={side.platform} size={14} />
                <span className="text-[13px] font-medium text-gray-900">{label}</span>
                {time && post.perPlatform.length > 1 && (
                  <span className="text-[12px] text-gray-500 tabular-nums">{time}</span>
                )}
                {side.permalink && (
                  <a
                    href={side.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-[12px] text-gray-500 hover:text-gray-900 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    Open on {label}
                    <ArrowUpRightIcon className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              <dl className="grid grid-cols-4 gap-3">
                {side.metrics.map((m) => (
                  <div key={m.label}>
                    <dt className="text-[11px] text-gray-500">{m.label}</dt>
                    <dd className="text-[14px] font-medium text-gray-900 tabular-nums">
                      {m.value.toLocaleString()}
                    </dd>
                  </div>
                ))}
                <div>
                  <dt className="text-[11px] text-gray-500">Engagement</dt>
                  <dd className="text-[14px] font-medium text-gray-900 tabular-nums">
                    {side.engagement.toLocaleString()}
                  </dd>
                </div>
              </dl>
            </section>
          );
        })}
      </div>
    </div>
  );
};

/** The post's image whole, on a quiet surface; the image icon when it has
 *  none or the link expired. */
const PostImage: React.FC<{ url: string | null }> = ({ url }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className="w-full h-[280px] rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden">
      {url && !broken ? (
        <img src={url} alt="" className="max-w-full max-h-full object-contain" onError={() => setBroken(true)} />
      ) : (
        <ImageIcon className="w-10 h-10 text-gray-300" />
      )}
    </div>
  );
};
